// SystemDiagram — hand-drawn SVG of the whole data architecture, in the Tally
// visual language: warm cream, terracotta accent, dusty-teal storage, stone
// neutrals, thin rounded strokes. Colours are the shared design tokens (hexes,
// since this is a fixed light theme). Responsive via viewBox; scales to width.

const INK = "#1c1917";
const MUTED = "#57534e";
const SUBTLE = "#a8a29e";
const LINE = "#e7e5e4";
const ACCENT = "#9a3412";
const ACCENT_SOFT = "#fed7aa";
const ACCENT_WASH = "#fff7ed";
const TEAL = "#5e7a73";

const COLS = [45, 135, 225, 315]; // node centres
const NODE_X = [6, 96, 186, 276];
const NODE_W = 78;

const SOURCES = ["Galaxy Watch", "Garmin", "Withings", "Food log"];
const TRANSPORT = [
  { label: "Bridge app", sub: "secret" },
  { label: "Garmin cloud", sub: "OAuth" },
  { label: "Withings cloud", sub: "OAuth" },
  { label: "Claude API", sub: "AI" },
];

export function SystemDiagram() {
  return (
    <svg
      viewBox="0 0 360 452"
      width="100%"
      role="img"
      aria-label="Architecture: device sources travel via the bridge app or cloud OAuth into the Cloudflare Worker, which stores data in D1 and R2 and serves the dashboard behind Access."
      className="h-auto select-none"
      fontFamily="ui-sans-serif, system-ui, sans-serif"
    >
      <defs>
        <marker id="arrow" markerWidth="8" markerHeight="8" refX="5.5" refY="3" orient="auto" markerUnits="userSpaceOnUse">
          <path d="M0,0 L6,3 L0,6 Z" fill={SUBTLE} />
        </marker>
      </defs>

      {/* ── 1 · Sources ── */}
      <text x="6" y="14" fontSize="8.5" letterSpacing="1" fill={SUBTLE}>SOURCES &amp; INPUTS</text>
      {SOURCES.map((label, i) => (
        <g key={label}>
          <rect x={NODE_X[i]} y={20} width={NODE_W} height={34} rx={8} fill="#fff" stroke={LINE} />
          <text x={COLS[i]} y={41} fontSize="10" textAnchor="middle" fill={INK}>{label}</text>
        </g>
      ))}
      {COLS.map((cx) => (
        <line key={cx} x1={cx} y1={54} x2={cx} y2={82} stroke={SUBTLE} markerEnd="url(#arrow)" />
      ))}

      {/* ── 2 · Transport (caption omitted — it would sit on the connectors) ── */}
      {TRANSPORT.map((t, i) => {
        // The bridge app is the piece you build/sideload — highlight it.
        const hot = i === 0;
        return (
          <g key={t.label}>
            <rect
              x={NODE_X[i]}
              y={84}
              width={NODE_W}
              height={34}
              rx={8}
              fill={hot ? ACCENT_WASH : "#fff"}
              stroke={hot ? ACCENT : LINE}
              strokeDasharray={hot ? "3 2" : undefined}
            />
            <text x={COLS[i]} y={101} fontSize="9" textAnchor="middle" fill={hot ? ACCENT : INK}>{t.label}</text>
            <text x={COLS[i]} y={111} fontSize="7.5" textAnchor="middle" fill={SUBTLE}>{t.sub}</text>
          </g>
        );
      })}
      {COLS.map((cx) => (
        <line key={cx} x1={cx} y1={118} x2={cx} y2={150} stroke={SUBTLE} markerEnd="url(#arrow)" />
      ))}

      {/* ── 3 · The Worker ── */}
      <rect x={10} y={152} width={340} height={140} rx={12} fill={ACCENT_WASH} stroke={ACCENT} strokeWidth={1.4} />
      <text x={24} y={172} fontSize="11" fontWeight="600" fill={ACCENT}>THE WORKER</text>
      <text x={120} y={172} fontSize="9" fill={MUTED}>Cloudflare · Hono</text>

      <rect x={24} y={180} width={312} height={34} rx={7} fill="#fff" stroke={ACCENT_SOFT} />
      <text x={34} y={195} fontSize="9" fontWeight="600" fill={INK}>Side door · /ingest · /webhooks · /auth</text>
      <text x={34} y={207} fontSize="8" fill={SUBTLE}>secret / OAuth — no login · dedups by source</text>

      <rect x={24} y={220} width={312} height={34} rx={7} fill="#fff" stroke={ACCENT_SOFT} />
      <text x={34} y={235} fontSize="9" fontWeight="600" fill={INK}>Front door · /api/*</text>
      <text x={34} y={247} fontSize="8" fill={SUBTLE}>behind Cloudflare Access — only your login</text>

      <text x={34} y={275} fontSize="8.5" fill={MUTED}>Cron · hourly rollup + 7am coach · weekly backup</text>

      {/* Worker → storage */}
      <line x1={110} y1={292} x2={110} y2={316} stroke={SUBTLE} markerEnd="url(#arrow)" />
      <line x1={250} y1={292} x2={250} y2={316} stroke={SUBTLE} markerEnd="url(#arrow)" />

      {/* ── 4 · Storage ── */}
      <text x="6" y="312" fontSize="8.5" letterSpacing="1" fill={SUBTLE}>STORED IN</text>
      <rect x={50} y={318} width={120} height={40} rx={8} fill="#fff" stroke={TEAL} />
      <text x={110} y={336} fontSize="10" fontWeight="600" textAnchor="middle" fill={TEAL}>D1 database</text>
      <text x={110} y={348} fontSize="8" textAnchor="middle" fill={SUBTLE}>every metric</text>
      <rect x={190} y={318} width={120} height={40} rx={8} fill="#fff" stroke={TEAL} />
      <text x={250} y={336} fontSize="10" fontWeight="600" textAnchor="middle" fill={TEAL}>R2 storage</text>
      <text x={250} y={348} fontSize="8" textAnchor="middle" fill={SUBTLE}>photos · backups</text>

      {/* Storage → app */}
      <line x1={110} y1={358} x2={170} y2={392} stroke={SUBTLE} markerEnd="url(#arrow)" />
      <line x1={250} y1={358} x2={190} y2={392} stroke={SUBTLE} markerEnd="url(#arrow)" />

      {/* ── 5 · The app ── */}
      <text x="6" y="386" fontSize="8.5" letterSpacing="1" fill={SUBTLE}>YOU SEE</text>
      <rect x={70} y={394} width={220} height={44} rx={10} fill="#fff" stroke={INK} strokeOpacity={0.25} />
      <text x={180} y={414} fontSize="11" fontWeight="600" textAnchor="middle" fill={INK}>Your dashboard</text>
      <text x={180} y={427} fontSize="8" textAnchor="middle" fill={SUBTLE}>React on Cloudflare Pages · behind Access</text>
    </svg>
  );
}
